import { useState, useEffect, useCallback } from 'react';
import { API, formatWeight, naturalBoxSort, sortBoxesNatural, todayIST, displayDate } from '@/lib/api';
import { useSubmitGuard } from '@/lib/useSubmitGuard';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/ui/decimal-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Trash2, Send, Search, X } from 'lucide-react';

let tabCounter = 1;

const emptyTab = (type) => ({
  id: ++tabCounter,
  type,
  customerMobile: '', customer: null, customerLookupDone: false,
  customerName: '', customerAddress: '', salesman: '',
  saleEntries: [{ box_id: '', gold_weight_g: '' }],
  supplierName: '',
  purchaseEntries: [{ box_id: '', gold_weight_g: '' }],
  polyEntries: [{ box_id: '', weight_g: '' }],
  formKey: 0,
});

export default function DailyOpsPage() {
  const { user } = useAuth();
  const [opType, setOpType] = useState('sale');
  const [boxes, setBoxes] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [tabs, setTabs] = useState([emptyTab('sale')]);
  const [activeTabId, setActiveTabId] = useState(tabCounter);
  const { busy: loading, run } = useSubmitGuard('Please wait — your last entry is still being saved');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [bRes, smRes, supRes] = await Promise.all([
          API.get('/boxes', { params: { active_only: true } }),
          API.get('/salesmen'),
          API.get('/suppliers'),
        ]);
        setBoxes(sortBoxesNatural(bRes.data)); setSalesmen(smRes.data); setSuppliers(supRes.data);
      } catch (e) { console.error(e); }
    };
    fetchData();
  }, []);

  const getBoxPurity = useCallback((boxId) => {
    const box = boxes.find(b => b.box_id === boxId);
    return box?.gold_purity || '';
  }, [boxes]);

  const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];
  const updateTab = (id, updater) => {
    setTabs(prev => prev.map(t => t.id === id ? (typeof updater === 'function' ? updater(t) : { ...t, ...updater }) : t));
  };

  const addNewTab = () => {
    const nt = emptyTab(opType);
    setTabs(prev => [...prev, nt]);
    setActiveTabId(nt.id);
  };

  const closeTab = (id) => {
    if (tabs.length <= 1) return;
    const idx = tabs.findIndex(t => t.id === id);
    const newTabs = tabs.filter(t => t.id !== id);
    setTabs(newTabs);
    if (activeTabId === id) {
      setActiveTabId(newTabs[Math.min(idx, newTabs.length - 1)].id);
    }
  };

  // When op type changes, update all tabs
  useEffect(() => {
    setTabs(prev => prev.map(t => ({ ...t, type: opType })));
  }, [opType]);

  const lookupCustomer = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab || !tab.customerMobile || tab.customerMobile.length < 10) { toast.error('Enter valid mobile'); return; }
    try {
      const res = await API.get(`/customers/lookup/${tab.customerMobile}`);
      if (res.data.found) {
        updateTab(tabId, { customer: res.data.customer, customerName: res.data.customer.name, customerAddress: res.data.customer.address || '', customerLookupDone: true });
        toast.success('Customer found!');
      } else {
        updateTab(tabId, { customer: null, customerName: '', customerAddress: '', customerLookupDone: true });
        toast.info('New customer.');
      }
    } catch (e) { toast.error('Lookup failed'); }
  };

  const calcTotals = (entries) => {
    const totals = { '76': 0, '84': 0, '92': 0 };
    entries.forEach(e => {
      const w = parseFloat(e.gold_weight_g) || 0;
      const purity = getBoxPurity(e.box_id);
      if (totals[purity] !== undefined) totals[purity] += w;
    });
    return totals;
  };

  const validateGoldEntries = (entries) => {
    for (const e of entries) {
      if (!e.box_id) { toast.error('Select a box for all entries'); return false; }
      const w = parseFloat(e.gold_weight_g);
      if (!w || w <= 0) { toast.error('Gold weight must be greater than 0 for all entries'); return false; }
    }
    return true;
  };

  const handleSaleSubmit = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    if (!tab.customerMobile || !tab.customerName) { toast.error('Enter customer details'); return; }
    if (!tab.salesman) { toast.error('Select salesman'); return; }
    const validEntries = tab.saleEntries.filter(e => e.box_id && e.gold_weight_g);
    if (validEntries.length === 0) { toast.error('Add at least one entry'); return; }
    if (!validateGoldEntries(validEntries)) return;
    return run(async () => {
      try {
        const endpoint = opType === 'sale' ? '/bills' : '/sale-returns';
        await API.post(endpoint, {
          customer_name: tab.customerName, customer_mobile: tab.customerMobile, customer_address: tab.customerAddress,
          entries: validEntries.map(e => ({ box_id: e.box_id, gold_weight_g: parseFloat(e.gold_weight_g), gold_purity: getBoxPurity(e.box_id) })),
          salesman_name: tab.salesman, submitted_by: user?.role || 'stock_manager',
        });
        const typeName = opType === 'sale' ? 'Sale' : 'Sale Return';
        const summary = validEntries.map(e => `${e.box_id}: ${e.gold_weight_g}g (${getBoxPurity(e.box_id)})`).join(', ');
        toast.success(`${typeName} recorded — ${summary}`, { duration: 5000 });
        updateTab(tabId, t => ({ ...t, customerMobile: '', customer: null, customerLookupDone: false, customerName: '', customerAddress: '', salesman: '', saleEntries: [{ box_id: '', gold_weight_g: '' }], formKey: t.formKey + 1 }));
        // Refresh boxes to get updated weights
        try { const bRes = await API.get('/boxes', { params: { active_only: true } }); setBoxes(sortBoxesNatural(bRes.data)); } catch {}
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handlePurchaseSubmit = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    if (!tab.supplierName) { toast.error('Select karigar'); return; }
    const validEntries = tab.purchaseEntries.filter(e => e.box_id && e.gold_weight_g);
    if (validEntries.length === 0) { toast.error('Add at least one entry'); return; }
    if (!validateGoldEntries(validEntries)) return;
    return run(async () => {
      try {
        if (opType === 'purchase_return') {
          // Single atomic request — all entries validated server-side before anything is written
          await API.post('/operations/batch', {
            entries: validEntries.map(e => ({
              box_id: e.box_id, operation_type: 'purchase_return',
              gold_weight_g: parseFloat(e.gold_weight_g), gold_purity: getBoxPurity(e.box_id),
              supplier_name: tab.supplierName, date: todayIST(), submitted_by: user?.role || 'stock_manager',
            })),
          });
        } else {
          await API.post('/purchases', {
            supplier_name: tab.supplierName,
            entries: validEntries.map(e => ({ box_id: e.box_id, gold_weight_g: parseFloat(e.gold_weight_g), gold_purity: getBoxPurity(e.box_id) })),
            submitted_by: user?.role || 'stock_manager',
          });
        }
        const typeName = opType === 'purchase' ? 'Purchase' : 'Purchase Return';
        const summary = validEntries.map(e => `${e.box_id}: ${e.gold_weight_g}g (${getBoxPurity(e.box_id)})`).join(', ');
        toast.success(`${typeName} recorded — ${summary}`, { duration: 5000 });
        updateTab(tabId, t => ({ ...t, supplierName: '', purchaseEntries: [{ box_id: '', gold_weight_g: '' }], formKey: t.formKey + 1 }));
        try { const bRes = await API.get('/boxes', { params: { active_only: true } }); setBoxes(sortBoxesNatural(bRes.data)); } catch {}
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const handlePolySubmit = async (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab) return;
    const polyOpType = opType === 'poly_add' ? 'poly_add' : 'poly_subtract';
    const validEntries = tab.polyEntries.filter(e => e.box_id && e.weight_g);
    if (validEntries.length === 0) { toast.error('Add at least one entry'); return; }
    for (const e of validEntries) {
      const w = parseFloat(e.weight_g);
      if (!w || w <= 0) { toast.error('Weight must be greater than 0'); return; }
    }
    return run(async () => {
      try {
        // Single atomic request — all entries validated server-side before anything is written
        await API.post('/operations/batch', {
          entries: validEntries.map(entry => ({
            box_id: entry.box_id, operation_type: polyOpType,
            polyethene_weight_g: parseFloat(entry.weight_g), gold_weight_g: 0,
            date: todayIST(), submitted_by: user?.role || 'stock_manager',
          })),
        });
        const typeName = opType === 'poly_add' ? 'Poly Add' : 'Poly Subtract';
        const summary = validEntries.map(e => `${e.box_id}: ${e.weight_g}g`).join(', ');
        toast.success(`${typeName} done — ${summary}`, { duration: 5000 });
        updateTab(tabId, t => ({ ...t, polyEntries: [{ box_id: '', weight_g: '' }], formKey: t.formKey + 1 }));
        try { const bRes = await API.get('/boxes', { params: { active_only: true } }); setBoxes(sortBoxesNatural(bRes.data)); } catch {}
      } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    });
  };

  const isSaleTab = opType === 'sale' || opType === 'sale_return';
  const isPurchaseTab = opType === 'purchase' || opType === 'purchase_return';
  const isPolyTab = opType === 'poly_add' || opType === 'poly_less';

  const renderSaleForm = (tab) => {
    const totals = calcTotals(tab.saleEntries);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          {['76', '84', '92'].map(p => (
            <Card key={p}><CardContent className="pt-3 pb-3 px-2 md:pt-4 md:px-4 text-center">
              <span className={`status-badge purity-${p} text-xs mb-1`}>{p}</span>
              <p className="kpi-number text-base md:text-lg mt-1">{totals[p].toFixed(3)}g</p>
            </CardContent></Card>
          ))}
        </div>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Customer</CardTitle></CardHeader>
          <CardContent>
            <div className="flex gap-2 mb-3">
              <Input placeholder="Mobile number" value={tab.customerMobile}
                onChange={e => updateTab(tab.id, { customerMobile: e.target.value, customerLookupDone: false })}
                className="flex-1 font-mono" maxLength={10} data-testid="sale-customer-mobile" />
              <Button onClick={() => lookupCustomer(tab.id)} variant="outline" className="flex-shrink-0" data-testid="customer-lookup-btn">
                <Search size={16} className="mr-1 hidden sm:inline" />Lookup
              </Button>
            </div>
            {tab.customerLookupDone && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="text-xs font-medium mb-1 block">Name</label>
                  <Input value={tab.customerName} onChange={e => updateTab(tab.id, { customerName: e.target.value })} data-testid="customer-name" /></div>
                <div><label className="text-xs font-medium mb-1 block">Address</label>
                  <Input value={tab.customerAddress} onChange={e => updateTab(tab.id, { customerAddress: e.target.value })} data-testid="customer-address" /></div>
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Items</CardTitle>
            <Button variant="outline" size="sm" onClick={() => updateTab(tab.id, t => ({ ...t, saleEntries: [...t.saleEntries, { box_id: '', gold_weight_g: '' }] }))} data-testid="add-sale-entry-btn">
              <Plus size={14} className="mr-1" />Add
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {tab.saleEntries.map((entry, i) => (
                <div key={`${tab.formKey}-${i}`} className="flex flex-col sm:flex-row gap-2 p-2 sm:p-0 border sm:border-0 rounded-lg sm:rounded-none" data-testid={`gold-entry-row-${i}`}>
                  <Select value={entry.box_id} onValueChange={v => updateTab(tab.id, t => { const u = [...t.saleEntries]; u[i] = { ...u[i], box_id: v }; return { ...t, saleEntries: u }; })}>
                    <SelectTrigger className="flex-1 sm:w-48" data-testid={`entry-box-select-${i}`}><SelectValue placeholder="Select box" /></SelectTrigger>
                    <SelectContent>{boxes.map(b => <SelectItem key={b.box_id} value={b.box_id}>{b.box_id} ({b.gold_purity}) {b.description ? `- ${b.description}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2 items-center">
                    <DecimalInput value={entry.gold_weight_g}
                      onChange={v => updateTab(tab.id, t => { const u = [...t.saleEntries]; u[i] = { ...u[i], gold_weight_g: v }; return { ...t, saleEntries: u }; })}
                      placeholder="Weight (g)" className="w-28" data-testid={`entry-weight-${i}`} />
                    <span className="text-xs text-muted-foreground w-6 text-center font-mono">{entry.box_id ? getBoxPurity(entry.box_id) : '--'}</span>
                    {tab.saleEntries.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => updateTab(tab.id, t => ({ ...t, saleEntries: t.saleEntries.filter((_, idx) => idx !== i) }))}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:justify-end">
              <div className="sm:w-48">
                <Select value={tab.salesman} onValueChange={v => updateTab(tab.id, { salesman: v })}>
                  <SelectTrigger data-testid="salesman-select"><SelectValue placeholder="Select salesman" /></SelectTrigger>
                  <SelectContent>
                    {salesmen.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    {salesmen.length === 0 && <SelectItem value="none" disabled>No salesmen added</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => handleSaleSubmit(tab.id)} disabled={loading} className="w-full sm:w-auto" data-testid="sale-submit-btn">
                <Send size={16} className="mr-2" />{loading ? 'Saving…' : (opType === 'sale' ? 'Send to Billing' : 'Submit Return')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPurchaseForm = (tab) => {
    const totals = calcTotals(tab.purchaseEntries);
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          {['76', '84', '92'].map(p => (
            <Card key={p}><CardContent className="pt-3 pb-3 px-2 md:pt-4 md:px-4 text-center">
              <span className={`status-badge purity-${p} text-xs mb-1`}>{p}</span>
              <p className="kpi-number text-base md:text-lg mt-1">{totals[p].toFixed(3)}g</p>
            </CardContent></Card>
          ))}
        </div>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Karigar</CardTitle></CardHeader>
          <CardContent>
            <Select value={tab.supplierName} onValueChange={v => updateTab(tab.id, { supplierName: v })}>
              <SelectTrigger className="w-full sm:max-w-xs" data-testid="supplier-select"><SelectValue placeholder="Select karigar" /></SelectTrigger>
              <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">Items</CardTitle>
            <Button variant="outline" size="sm" onClick={() => updateTab(tab.id, t => ({ ...t, purchaseEntries: [...t.purchaseEntries, { box_id: '', gold_weight_g: '' }] }))} data-testid="add-purchase-entry-btn">
              <Plus size={14} className="mr-1" />Add
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {tab.purchaseEntries.map((entry, i) => (
                <div key={`${tab.formKey}-${i}`} className="flex flex-col sm:flex-row gap-2 p-2 sm:p-0 border sm:border-0 rounded-lg sm:rounded-none">
                  <Select value={entry.box_id} onValueChange={v => updateTab(tab.id, t => { const u = [...t.purchaseEntries]; u[i] = { ...u[i], box_id: v }; return { ...t, purchaseEntries: u }; })}>
                    <SelectTrigger className="flex-1 sm:w-48"><SelectValue placeholder="Select box" /></SelectTrigger>
                    <SelectContent>{boxes.map(b => <SelectItem key={b.box_id} value={b.box_id}>{b.box_id} ({b.gold_purity}) {b.description ? `- ${b.description}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="flex gap-2 items-center">
                    <DecimalInput value={entry.gold_weight_g}
                      onChange={v => updateTab(tab.id, t => { const u = [...t.purchaseEntries]; u[i] = { ...u[i], gold_weight_g: v }; return { ...t, purchaseEntries: u }; })}
                      placeholder="Weight (g)" className="w-28" />
                    <span className="text-xs text-muted-foreground w-6 text-center font-mono">{entry.box_id ? getBoxPurity(entry.box_id) : '--'}</span>
                    {tab.purchaseEntries.length > 1 && (
                      <Button variant="ghost" size="icon" onClick={() => updateTab(tab.id, t => ({ ...t, purchaseEntries: t.purchaseEntries.filter((_, idx) => idx !== i) }))}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={() => handlePurchaseSubmit(tab.id)} disabled={loading} className="w-full sm:w-auto" data-testid="purchase-submit-btn">
                <Send size={16} className="mr-2" />{loading ? 'Saving…' : (opType === 'purchase' ? 'Submit Purchase' : 'Submit Return')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderPolyForm = (tab) => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base">{opType === 'poly_add' ? 'Add Polythene' : 'Remove Polythene'}</CardTitle>
          <Button variant="outline" size="sm" onClick={() => updateTab(tab.id, t => ({ ...t, polyEntries: [...t.polyEntries, { box_id: '', weight_g: '' }] }))} data-testid="add-poly-entry-btn">
            <Plus size={14} className="mr-1" />Add
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {tab.polyEntries.map((entry, i) => (
              <div key={`${tab.formKey}-${i}`} className="flex flex-col sm:flex-row gap-2 p-2 sm:p-0 border sm:border-0 rounded-lg sm:rounded-none">
                <Select value={entry.box_id} onValueChange={v => updateTab(tab.id, t => { const u = [...t.polyEntries]; u[i] = { ...u[i], box_id: v }; return { ...t, polyEntries: u }; })}>
                  <SelectTrigger className="flex-1 sm:w-48"><SelectValue placeholder="Select box" /></SelectTrigger>
                  <SelectContent>{boxes.map(b => <SelectItem key={b.box_id} value={b.box_id}>{b.box_id} ({b.gold_purity}) {b.description ? `- ${b.description}` : ""}</SelectItem>)}</SelectContent>
                </Select>
                <div className="flex gap-2 items-center">
                  <DecimalInput value={entry.weight_g}
                    onChange={v => updateTab(tab.id, t => { const u = [...t.polyEntries]; u[i] = { ...u[i], weight_g: v }; return { ...t, polyEntries: u }; })}
                    placeholder="Poly wt (g)" className="w-28" />
                  {tab.polyEntries.length > 1 && (
                    <Button variant="ghost" size="icon" onClick={() => updateTab(tab.id, t => ({ ...t, polyEntries: t.polyEntries.filter((_, idx) => idx !== i) }))}>
                      <Trash2 size={14} className="text-destructive" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={() => handlePolySubmit(tab.id)} disabled={loading} className="w-full sm:w-auto" data-testid="poly-submit-btn">
              <Send size={16} className="mr-2" />{loading ? 'Saving…' : (opType === 'poly_add' ? 'Add Polythene' : 'Remove Polythene')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1>Daily Operations</h1>
        <p className="hidden sm:block">Record sales, purchases, returns, and polythene ops</p>
      </div>

      {/* Operation Type Tabs */}
      <Tabs value={opType} onValueChange={setOpType}>
        <TabsList className="mb-4 w-full grid grid-cols-3 sm:grid-cols-6">
          <TabsTrigger value="sale" className="text-xs sm:text-sm" data-testid="tab-sale">Sale</TabsTrigger>
          <TabsTrigger value="sale_return" className="text-xs sm:text-sm" data-testid="tab-sale-return">Return</TabsTrigger>
          <TabsTrigger value="purchase" className="text-xs sm:text-sm" data-testid="tab-purchase">Purchase</TabsTrigger>
          <TabsTrigger value="purchase_return" className="text-xs sm:text-sm" data-testid="tab-purchase-return">P.Return</TabsTrigger>
          <TabsTrigger value="poly_add" className="text-xs sm:text-sm" data-testid="tab-poly-add">Poly Add</TabsTrigger>
          <TabsTrigger value="poly_less" className="text-xs sm:text-sm" data-testid="tab-poly-less">Poly Less</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Chrome-like entry tabs */}
      <div className="flex items-center gap-1 mb-4 overflow-x-auto pb-1" data-testid="entry-tabs-bar">
        {tabs.map((tab, idx) => (
          <button key={tab.id}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-t-lg text-xs sm:text-sm font-medium border border-b-0 whitespace-nowrap transition-colors ${activeTabId === tab.id ? 'bg-card text-foreground border-border' : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted'}`}
            onClick={() => setActiveTabId(tab.id)}
            data-testid={`entry-tab-${idx}`}
          >
            Entry {idx + 1}
            {tabs.length > 1 && (
              <span onClick={e => { e.stopPropagation(); closeTab(tab.id); }}
                className="ml-1 hover:text-destructive cursor-pointer rounded-full p-0.5 hover:bg-destructive/10" data-testid={`close-tab-${idx}`}>
                <X size={12} />
              </span>
            )}
          </button>
        ))}
        <button onClick={addNewTab}
          className="flex items-center justify-center w-7 h-7 rounded-lg border border-dashed text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          data-testid="add-entry-tab-btn" title="New entry tab">
          <Plus size={14} />
        </button>
      </div>

      {/* Active tab form */}
      {activeTab && (
        <div data-testid="active-entry-form">
          {isSaleTab && renderSaleForm(activeTab)}
          {isPurchaseTab && renderPurchaseForm(activeTab)}
          {isPolyTab && renderPolyForm(activeTab)}
        </div>
      )}
    </div>
  );
}
